import type { CurrentUser, LoginInput, LoginResponse } from '@fdt/shared';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, refreshSession, setSessionLostHandler } from '../api/client';
import { tokenStorage } from './token-storage';

interface AuthState {
  user: CurrentUser | null;
  /** True until the stored refresh token has been checked on first load. */
  initialising: boolean;
  login: (input: LoginInput) => Promise<CurrentUser>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [initialising, setInitialising] = useState(true);

  // Restore the session from the stored refresh token on first load.
  useEffect(() => {
    let cancelled = false;

    async function restore(): Promise<void> {
      if (!tokenStorage.getRefreshToken()) {
        setInitialising(false);
        return;
      }
      try {
        // Through the shared single-flight refresh: StrictMode runs this effect
        // twice in development, and two refreshes with the same token would look
        // like a replay and revoke every session.
        await refreshSession();
        const me = await api.get<CurrentUser>('/auth/me');
        if (!cancelled) {
          setUser(me.data);
        }
      } catch {
        tokenStorage.clear();
      } finally {
        if (!cancelled) {
          setInitialising(false);
        }
      }
    }

    void restore();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setSessionLostHandler(() => setUser(null));
  }, []);

  const login = useCallback(async (input: LoginInput) => {
    const { data } = await api.post<LoginResponse>('/auth/login', input);
    tokenStorage.setAccessToken(data.accessToken);
    tokenStorage.setRefreshToken(data.refreshToken);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = tokenStorage.getRefreshToken();
    if (refreshToken) {
      // A failing logout must not trap the user in the app.
      await api.post('/auth/logout', { refreshToken }).catch(() => undefined);
    }
    tokenStorage.clear();
    setUser(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, initialising, login, logout }),
    [user, initialising, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return context;
}
